import { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import BigNumber from 'bignumber.js'
import { formatAmount } from '../../helpers'
import RateGreenDot from './RateGreenDot'
import IconSwitch from '~/assets/images/switch-round.svg'
import {
  StyledRateContainer,
  StyledRateWrapper,
  StyledRateDetail,
  SwitchIcon,
} from './styled'
import { RateState } from '../../types'
import { useGA } from '~/hooks'
import { useSpotSwapPair, useSpotSwapUserConfig } from '~/state/dex/hooks'
import Loading from './Loading'

interface RateDetailProps {
  rate: string
  rateState: RateState
}

const RateDetail: React.FC<RateDetailProps> = ({ rate, rateState }) => {
  const { t } = useTranslation()
  const [inputToken, outputToken] = useSpotSwapPair()
  const { isCrossSwap } = useSpotSwapUserConfig()
  const inputTokenRef = useRef(inputToken?.address)
  const outputTokenRef = useRef(outputToken?.address)
  const [rateLiteral, setRateLiteral] = useState('')
  const [scale, setScale] = useState(6)
  const [leftToken, setLeftToken] = useState<SpotSwapToken>(inputToken)
  const [rightToken, setRightToken] = useState<SpotSwapToken>(outputToken)
  const [isInputRateDirection, setIsInputRateDirection] = useState(true)

  const outputRate = useMemo(() => {
    return new BigNumber(1).dividedBy(rate).toFixed()
  }, [rate])

  const { trackClick } = useGA()
  // change direction
  useEffect(() => {
    if (
      inputTokenRef.current === outputToken?.address &&
      outputTokenRef.current === inputToken?.address
    ) {
      inputTokenRef.current = inputToken?.address
      outputTokenRef.current = outputToken?.address
      setIsInputRateDirection(true)
    }
  }, [inputToken, outputToken])

  useEffect(() => {
    const displayRate = isInputRateDirection ? rate : outputRate
    // 对于 SHIB BTT SPELL 等小币种汇率过小，保留小数点后12位确保汇率不会显示成0.00，其他币种保留6位
    const calcScale = +displayRate < 1e-6 ? 12 : 6
    setScale(calcScale)
    setRateLiteral(Number(displayRate) ? displayRate : '~')
    setLeftToken(isInputRateDirection ? inputToken : outputToken)
    setRightToken(isInputRateDirection ? outputToken : inputToken)
  }, [rate, inputToken, outputToken, isInputRateDirection])

  const onSwitch = () => {
    trackClick('webtl_instant_rateswitch', { is_cross_swap: isCrossSwap })
    setIsInputRateDirection(!isInputRateDirection)
  }

  const showSkeleton =
    rateState === RateState.LOADING || rateState === RateState.IDLE

  return (
    <StyledRateContainer>
      <span>{t('best_price')}</span>
      {showSkeleton ? (
        <Loading />
      ) : (
        <StyledRateWrapper gap={4} align="center">
          <RateGreenDot rateState={rateState} />
          <StyledRateDetail gap={4} align="center">
            <span>1 {leftToken?.symbol}</span>
            <SwitchIcon onClick={onSwitch} src={IconSwitch} />
            <span className="boldText">
              {formatAmount(
                rateLiteral,
                Math.min(scale, rightToken?.decimals ?? scale),
              )}{' '}
              {rightToken?.symbol}
            </span>
          </StyledRateDetail>
        </StyledRateWrapper>
      )}
    </StyledRateContainer>
  )
}

export default RateDetail
